import { useParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UniversalHeader } from './UniversalHeader';
import { PlanTab } from '@/features/plans/components/PlanTab';
import { CategorySplitView } from '@/features/categories/components/CategorySplitView';
import { ChannelTab } from '@/features/channels/components/ChannelTab';
// Removed deprecated version changes and compare features
import { useVersionStatus } from '@/hooks/useVersion';
import type { VersionAwareProps } from '../types/props';
// Removed deprecated ContentGroupTab import
import { TagTab } from "@/features/tags/components/TagTab";
import { useEffect, useState, memo, useCallback } from 'react';
import { logger, LOG_CONTEXTS } from '@/utils/logger';

// Import our new database enhancement components
import { DeviceManagement } from '@/features/providers/components/DeviceManagement';
import { StreamingFeaturesManagement } from '@/features/providers/components/StreamingFeaturesManagement';
import { PromotionalOffersManagement } from '@/features/providers/components/PromotionalOffersManagement';
import { AdditionalFeesManagement } from '@/features/providers/components/AdditionalFeesManagement';
import { ContentCoverageManagement } from '@/features/providers/components/ContentCoverageManagement';
import { ServiceTermsManagement } from '@/features/providers/components/ServiceTermsManagement';
import { ServiceQualityManagement } from '@/features/providers/components/ServiceQualityManagement';
import { VersionChanges } from './changes/VersionChanges';
import { PackagesPage } from '@/features/packages/pages';

// Component instance counter to track multiple instances
let instanceCounter = 0;

// Memoize the ChannelTab component to prevent unnecessary re-renders
const MemoizedChannelTab = memo(ChannelTab);

// Memoize tab components
const MemoizedPlanTab = memo(PlanTab);
const MemoizedCategorySplitView = memo(CategorySplitView);
const MemoizedTagTab = memo(TagTab);
// Removed deprecated: MemoizedContentGroupTab, MemoizedVersionChanges, MemoizedVersionCompare

// Memoize our new database enhancement components
const MemoizedDeviceManagement = memo(DeviceManagement);
const MemoizedStreamingFeaturesManagement = memo(StreamingFeaturesManagement);
const MemoizedPromotionalOffersManagement = memo(PromotionalOffersManagement);
const MemoizedAdditionalFeesManagement = memo(AdditionalFeesManagement);
const MemoizedContentCoverageManagement = memo(ContentCoverageManagement);
const MemoizedServiceTermsManagement = memo(ServiceTermsManagement);
const MemoizedServiceQualityManagement = memo(ServiceQualityManagement);
const MemoizedVersionChanges = memo(VersionChanges);
const MemoizedPackagesPage = memo(PackagesPage);

export function VersionDashboard() {
  // Create a unique instance ID for this component instance
  const [instanceId] = useState(() => {
    instanceCounter++;
    return `VersionDashboard_${instanceCounter}`;
  });
  
  const { providerId, versionId, regionId } = useParams();
  
  // Log component mount and params
  useEffect(() => {
    logger.info(LOG_CONTEXTS.RENDER, `Component mounted: ${instanceId}`, {
      providerId,
      versionId,
      regionId
    });
    
    return () => {
      logger.info(LOG_CONTEXTS.RENDER, `Component unmounted: ${instanceId}`);
    };
  }, [instanceId, providerId, versionId, regionId]);
  
  const { data: statusData } = useVersionStatus(
    Number(providerId), 
    Number(versionId)
  );
  
  // Log when status data changes
  useEffect(() => {
    logger.debug(LOG_CONTEXTS.RENDER, `Status data updated in ${instanceId}`, {
      isPublished: statusData?.isPublished,
      hasStatusData: !!statusData
    });
  }, [statusData, instanceId]);

  const versionProps: VersionAwareProps = {
    providerId: Number(providerId),
    versionId: Number(versionId),
    regionId: regionId ? Number(regionId) : undefined,
    isReadOnly: statusData?.isPublished ?? false
  };
  
  // Track active tab for logging
  const [activeTab, setActiveTab] = useState('channels');
  
  const handleTabChange = useCallback((value: string) => {
    logger.info(LOG_CONTEXTS.RENDER, `Tab changed in ${instanceId}`, {
      from: activeTab,
      to: value
    });
    
    setActiveTab(value);
  }, [activeTab, instanceId]);
  
  // Log component render
  logger.trackRender(`${instanceId} (providerId: ${providerId}, versionId: ${versionId}, regionId: ${regionId})`);

  return (
    <div className="space-y-6">
      <UniversalHeader {...versionProps} />

      <Tabs defaultValue="channels" onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="channels">Channels</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="packages">Packages</TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="offers">Offers</TabsTrigger>
          <TabsTrigger value="fees">Fees</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="terms">Terms</TabsTrigger>
          <TabsTrigger value="quality">Quality</TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
          <TabsTrigger value="changes">Changes</TabsTrigger>
          {/* Removed deprecated tabs: Content Groups, Compare */}
        </TabsList>

        <TabsContent value="plans" className="mt-6">
          {activeTab === 'plans' && <MemoizedPlanTab {...versionProps} />}
        </TabsContent>

        <TabsContent value="channels" className="mt-6">
          {activeTab === 'channels' && <MemoizedChannelTab {...versionProps} />}
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          {activeTab === 'categories' && <MemoizedCategorySplitView {...versionProps} />}
        </TabsContent>

        <TabsContent value="packages" className="mt-6">
          {activeTab === 'packages' && <MemoizedPackagesPage {...versionProps} />}
        </TabsContent>

        {/* Removed deprecated Content Groups tab */}

        <TabsContent value="features" className="mt-6">
          {activeTab === 'features' && (
            <MemoizedStreamingFeaturesManagement 
              providerId={versionProps.providerId} 
              versionId={versionProps.versionId}
              regionId={versionProps.regionId}
            />
          )}
        </TabsContent>

        <TabsContent value="devices" className="mt-6">
          {activeTab === 'devices' && (
            <MemoizedDeviceManagement 
              providerId={versionProps.providerId} 
              versionId={versionProps.versionId}
              regionId={versionProps.regionId}
            />
          )}
        </TabsContent>

        <TabsContent value="offers" className="mt-6">
          {activeTab === 'offers' && (
            <MemoizedPromotionalOffersManagement 
              providerId={versionProps.providerId} 
              versionId={versionProps.versionId}
              regionId={versionProps.regionId}
            />
          )}
        </TabsContent>

        <TabsContent value="fees" className="mt-6">
          {activeTab === 'fees' && versionProps.regionId && (
            <MemoizedAdditionalFeesManagement 
              providerId={versionProps.providerId} 
              versionId={versionProps.versionId}
              regionId={versionProps.regionId}
            />
          )}
          {activeTab === 'fees' && !versionProps.regionId && (
            <div className="text-center py-8 text-muted-foreground">
              Region ID is required for additional fees management
            </div>
          )}
        </TabsContent>

        <TabsContent value="content" className="mt-6">
          {activeTab === 'content' && (
            <MemoizedContentCoverageManagement 
              providerId={versionProps.providerId} 
              versionId={versionProps.versionId}
              regionId={versionProps.regionId}
            />
          )}
        </TabsContent>

        <TabsContent value="terms" className="mt-6">
          {activeTab === 'terms' && versionProps.regionId && (
            <MemoizedServiceTermsManagement 
              providerId={versionProps.providerId} 
              versionId={versionProps.versionId}
              regionId={versionProps.regionId}
            />
          )}
          {activeTab === 'terms' && !versionProps.regionId && (
            <div className="text-center py-8 text-muted-foreground">
              Region ID is required for Service Terms management.
            </div>
          )}
        </TabsContent>

        <TabsContent value="quality" className="mt-6">
          {activeTab === 'quality' && versionProps.regionId && (
            <MemoizedServiceQualityManagement 
              providerId={versionProps.providerId} 
              versionId={versionProps.versionId}
              regionId={versionProps.regionId}
            />
          )}
          {activeTab === 'quality' && !versionProps.regionId && (
            <div className="text-center py-8 text-muted-foreground">
              Region ID is required for Service Quality management.
            </div>
          )}
        </TabsContent>

        <TabsContent value="tags" className="mt-6">
          {activeTab === 'tags' && <MemoizedTagTab {...versionProps} />}
        </TabsContent>

        <TabsContent value="changes" className="mt-6">
          {activeTab === 'changes' && (
            <MemoizedVersionChanges
              providerId={versionProps.providerId}
              versionId={versionProps.versionId}
              isReadOnly={versionProps.isReadOnly}
            />
          )}
        </TabsContent>

        {/* Removed deprecated Compare tab */}
      </Tabs>
    </div>
  );
}